import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { constants as styleConstants } from "@/styles/config";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Formik } from "formik";
import * as Yup from "yup";

import { getCandidateProfile, saveCandidateProfile } from "@/services/candidateProfiles";
import CandidateProfileForm from "@/components/forms/CandidateProfileForm";

const VALID_REGIONS = ["Midwest", "Northeast", "Southeast", "Southwest", "West", "International"];

const validationSchema = Yup.object().shape({
  desired_base_salary: Yup.number().min(0).nullable(),
  desired_total_compensation: Yup.number().min(0).nullable(),
  desired_title: Yup.string().max(200).nullable(),
  desired_title_variner_value: Yup.string().max(200).nullable()
});

function buildInitialValues(profile) {
  return {
    high_potential: profile.high_potential ?? false,
    hiring_manager: profile.hiring_manager ?? false,
    desired_cities: profile.desired_cities ?? [],
    desired_states: profile.desired_states ?? [],
    desired_regions: (profile.desired_regions ?? []).filter((r) => VALID_REGIONS.includes(r)),
    desired_departments: profile.desired_departments ?? [],
    desired_functions: profile.desired_functions ?? [],
    desired_categories: profile.desired_categories ?? [],
    desired_industries: profile.desired_industries ?? [],
    desired_base_salary: profile.desired_base_salary ?? null,
    desired_total_compensation: profile.desired_total_compensation ?? null,
    desired_title: profile.desired_title ?? null,
    desired_title_variner_value: profile.desired_title_variner_value ?? null,
    open_to_remote: profile.open_to_remote ?? false,
    compensation_date: profile.compensation_date ?? null
  };
}

export default function CandidateProfileEdit() {
  const { candidateId } = useParams();
  const history = useHistory();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [syncWarning, setSyncWarning] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getCandidateProfile(candidateId);
        setProfile(res.data);
      } catch (e) {
        setError("Failed to load candidate profile.");
      } finally {
        setLoading(false);
      }
    })();
  }, [candidateId]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    setSaveSuccess(false);
    setSyncWarning(null);
    setError(null);
    try {
      const FIELDS = [
        "high_potential",
        "hiring_manager",
        "desired_cities",
        "desired_states",
        "desired_regions",
        "desired_departments",
        "desired_functions",
        "desired_categories",
        "desired_industries",
        "desired_base_salary",
        "desired_total_compensation",
        "desired_title",
        "desired_title_variner_value",
        "open_to_remote"
      ];

      const updates = {};
      for (const field of FIELDS) {
        updates[field] = values[field];
      }

      const res = await saveCandidateProfile(candidateId, updates);
      setProfile((prev) => ({ ...prev, ...res.data }));
      setSaveSuccess(true);
      if (res.data?.bullhornSyncWarning) setSyncWarning(res.data.bullhornSyncWarning);
      resetForm({ values: buildInitialValues({ ...profile, ...res.data }) });
    } catch (e) {
      setError("Failed to save. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !profile) {
    return (
      <Box p={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const bh = profile?.bullhorn ?? null;
  const name =
    bh?.firstName || bh?.lastName
      ? `${bh.firstName ?? ""} ${bh.lastName ?? ""}`.trim()
      : candidateId;

  return (
    <Box p={4} maxWidth={900} ml={`${styleConstants.desktopDrawerClosedWidth}px`}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push("/variner/candidate-profiles")}
          size="small"
        >
          All Candidates
        </Button>
        <Typography variant="h5" fontWeight={600}>
          {name}
        </Typography>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(false)}>
          Profile saved successfully.
        </Alert>
      )}
      {syncWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSyncWarning(null)}>
          {syncWarning}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Formik
        initialValues={buildInitialValues(profile)}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        {({ handleSubmit: formikSubmit, isSubmitting, dirty }) => (
          <form onSubmit={formikSubmit}>
            <CandidateProfileForm bullhorn={bh} />
            <Box mt={3} display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || !dirty}
                sx={{ minWidth: 140 }}
              >
                {isSubmitting ? <CircularProgress size={20} /> : "Save Changes"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
}
